import type { TCoreButtonStyleProps } from '@core/widgets';
import { memoByPalette, SemanticColor, TSemanticColors } from '../../../theme/colors';
import { TButtonOption } from '../../../types/options';
import { isMobileByTouch } from '../../../utils/helpers';
import { TButtonPillViewProps } from '../ButtonPillView';

type THierarchy = NonNullable<TButtonPillViewProps['hierarchy']>;

export const getPaddingPostfix = (hasIcon: boolean, iconPosition?: TButtonOption['iconPosition'], size?: TButtonOption['size']): string => {
    const prefix = size ? size.charAt(0).toUpperCase() + size.slice(1) : 'Medium';
    const postfix = hasIcon ? (iconPosition === 'right' ? 'IconRight' : 'IconLeft') : '';

    return prefix + postfix;
};

type TSize = NonNullable<TButtonPillViewProps['size']>;

/** Pill box height in px per size — shared by the default and the glass render paths. */
export const PILL_HEIGHT_MAP: Record<TSize, number> = {
    large: 56,
    medium: 40,
    small: 28,
};

/**
 * The server `size` is not type-checked at runtime: anything but a known size (unknown string, `null`) falls back to
 * `large`, the documented default, instead of indexing the size maps to `undefined` (collapsed pill, glass crash).
 */
export const resolvePillSize = (size: unknown): TSize => (typeof size === 'string' && size in PILL_HEIGHT_MAP ? (size as TSize) : 'large');

/** Label typography per size — shared by the default and the glass render paths. */
export const PILL_FONT_STYLE_MAP: Record<TSize, NonNullable<TCoreButtonStyleProps['fontStyle']>> = {
    large: { fontSize: 17, lineHeight: 24 },
    medium: { fontSize: 17, lineHeight: 24 },
    small: { fontSize: 14, lineHeight: 20 },
};

const buildHierarchyBgMap = (
    colors: TSemanticColors,
): Record<THierarchy, Pick<TCoreButtonStyleProps, 'main' | 'onMain' | 'outline' | 'hovered' | 'disabled' | 'pressed'>> => ({
    primary: {
        main: colors[SemanticColor.FillPrimary],
        onMain: colors[SemanticColor.TextPrimaryInverted],
        outline: colors[SemanticColor.BorderEmphasised],
        disabled: {
            main: colors[SemanticColor.FillPrimaryDisabled],
            onMain: colors[SemanticColor.TextPrimaryInvertedDisabled],
        },
        hovered: {
            main: colors[SemanticColor.FillPrimaryHovered],
        },
        pressed: {
            main: colors[SemanticColor.FillPrimaryPressed],
        },
    },
    primaryBlack: {
        main: 'var(--black-100)',
        onMain: 'var(--white-100)',
        outline: 'var(--white-100)',
        disabled: {
            main: 'var(--white-88-lc)',
            onMain: 'var(--white-100)',
        },
        hovered: {
            main: 'var(--gray-cold-16-lc)',
        },
        pressed: {
            main: 'var(--gray-cold-8-lc)',
        },
    },
    secondary: {
        main: colors[SemanticColor.FillSecondary],
        onMain: colors[SemanticColor.TextEmphasised],
        outline: colors[SemanticColor.BorderEmphasisedInvariable],
        disabled: {
            main: colors[SemanticColor.FillSecondaryDisabled],
            onMain: colors[SemanticColor.TextEmphasisedDisabled],
        },
        hovered: {
            main: colors[SemanticColor.FillSecondaryHovered],
        },
        pressed: {
            main: colors[SemanticColor.FillSecondaryPressed],
        },
    },
    secondaryWhite: {
        main: 'var(--white-100)',
        onMain: 'var(--black-100)',
        outline: 'var(--white-100)',
        disabled: {
            main: 'var(--gray-warm-16-lc)',
            onMain: 'var(--gray-warm-40-lc)',
        },
        hovered: {
            main: 'var(--gray-warm-16-lc)',
        },
        pressed: {
            main: 'var(--gray-warm-8-lc)',
        },
    },
    tertiary: {
        main: colors[SemanticColor.FillTertiary],
        onMain: colors[SemanticColor.TextPrimary],
        disabled: {
            main: colors[SemanticColor.FillTertiaryDisabled],
            onMain: colors[SemanticColor.TextPrimaryDisabled],
        },
        hovered: {
            main: colors[SemanticColor.FillTertiaryHovered],
        },
        pressed: {
            main: colors[SemanticColor.FillTertiaryPressed],
        },
    },
    tertiaryWhite: {
        main: 'var(--white-100)',
        onMain: 'var(--black-100)',
        disabled: {
            main: 'var(--gray-warm-16-lc)',
            onMain: 'var(--gray-warm-40-lc)',
        },
        hovered: {
            main: 'var(--gray-warm-16-lc)',
        },
        pressed: {
            main: 'var(--gray-warm-8-lc)',
        },
    },
    destructive: {
        main: colors[SemanticColor.FillDestructive],
        onMain: colors[SemanticColor.TextError],
        disabled: {
            main: colors[SemanticColor.FillDestructiveDisabled],
            onMain: colors[SemanticColor.TextErrorDisabled],
        },
        hovered: {
            main: colors[SemanticColor.FillDestructiveHovered],
        },
        pressed: {
            main: colors[SemanticColor.FillDestructivePressed],
        },
    },
    highlighted: {},
    tertiaryGlass: {},
});
export const getHierarchyBgMap = memoByPalette(buildHierarchyBgMap);

const buildHierarchyTextMap = (colors: TSemanticColors): Record<THierarchy, Pick<TCoreButtonStyleProps, 'main' | 'hovered' | 'disabled' | 'pressed'>> => ({
    primary: {
        main: colors[SemanticColor.TextPrimaryInverted],
        disabled: {
            main: colors[SemanticColor.TextPrimaryInvertedDisabled],
        },
    },
    primaryBlack: {
        main: 'var(--white-100)',
        disabled: {
            main: 'var(--white-100)',
        },
    },
    secondary: {
        main: colors[SemanticColor.TextEmphasised],
        disabled: {
            main: colors[SemanticColor.TextEmphasisedDisabled],
        },
    },
    secondaryWhite: {
        main: 'var(--black-100)',
        disabled: {
            main: 'var(--gray-warm-40-lc)',
        },
    },
    tertiary: {
        main: colors[SemanticColor.TextPrimary],
        disabled: {
            main: colors[SemanticColor.TextPrimaryDisabled],
        },
    },
    tertiaryWhite: {
        main: 'var(--black-100)',
        disabled: {
            main: 'var(--gray-warm-40-lc)',
        },
    },
    destructive: {
        main: colors[SemanticColor.TextError],
        disabled: {
            main: colors[SemanticColor.TextErrorDisabled],
        },
    },
    highlighted: {},
    tertiaryGlass: {},
});
export const getHierarchyTextMap = memoByPalette(buildHierarchyTextMap);

export const PADDING_MAP: Record<string, { top: number; right: number; bottom: number; left: number }> = {
    Large: { top: 10, right: 32, bottom: 10, left: 32 },
    LargeIconRight: { top: 10, right: 16, bottom: 10, left: 32 },
    LargeIconLeft: { top: 10, right: 32, bottom: 10, left: 16 },
    Medium: { top: 10, right: 24, bottom: 10, left: 24 },
    MediumIconRight: { top: 10, right: 16, bottom: 10, left: 16 },
    MediumIconLeft: { top: 10, right: 16, bottom: 10, left: 16 },
    Small: { top: 10, right: 16, bottom: 10, left: 16 },
    SmallIconRight: { top: 10, right: 8, bottom: 10, left: 16 },
    SmallIconLeft: { top: 10, right: 16, bottom: 10, left: 8 },
};

export const getMaxWidth = (maxWidth: TButtonPillViewProps['maxWidth']) =>
    maxWidth !== undefined ? maxWidth : isMobileByTouch() ? undefined : 'calc(((100vw - 168px) / 6) * 2 + 24px)';
